/**
 * DoctorDashboard — Panel de Control para el Médico de Familia (WE360)
 * Permite a los profesionales de la salud monitorear a los colaboradores de diferentes organizaciones,
 * visualizar sus 13 biomarcadores con un diseño tipo PageSpeed y gestionar alertas clínicas.
 */
import { useState, useEffect, useCallback, useMemo } from "react";
import { collection, getDocs, query, where, doc, updateDoc, getDoc } from "firebase/firestore";
import { db } from "@/firebase";
import { useUserProfile } from "@/contexts/UserProfileContext";
import { useAuth } from "@/contexts/AuthContext";
import {
  ShieldCheck, RefreshCw, Loader2, CheckCircle2, AlertTriangle,
  AlertCircle, Clock, User, Building2, ChevronRight, Info, Search,
  Filter, TrendingUp, Calendar, Heart, Activity, FileText, X,
  UserCheck, Weight, Ruler, ChevronDown, ChevronUp, Users,
  Stethoscope, ShieldAlert
} from "lucide-react";
import { listMeasurements } from "@/services/measurements";
import { generateSimulatedCohort } from "@/pages/Client/helpers";
import DoctorPopulationTable from "./DoctorPopulationTable";
import IhoCoordinationModal from "./IhoCoordinationModal";
import { extractSevenIndicators, calcDynamicTrend } from "./clinicalClusters";

// Configuración estética de las alertas
const SEVERITY_CONFIG = {
  critical: { label: "Crítico", bg: "#FEF2F2", border: "#FCA5A5", color: "#B91C1C", dot: "#DC2626", icon: AlertCircle },
  danger:   { label: "Peligro", bg: "#FFF7ED", border: "#FED7AA", color: "#C2410C", dot: "#F97316", icon: AlertTriangle },
  warning:  { label: "Atención", bg: "#FEFCE8", border: "#FEF08A", color: "#854D0E", dot: "#EAB308", icon: AlertTriangle },
};

function SeverityBadge({ severity }) {
  const cfg = SEVERITY_CONFIG[severity] || SEVERITY_CONFIG.warning;
  return (
    <span
      className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-bold"
      style={{ background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.border}` }}
    >
      <span className="w-1.5 h-1.5 rounded-full" style={{ background: cfg.dot }} />
      {cfg.label}
    </span>
  );
}

// Formateo elegante de fecha y hora
function fmtDateTime(raw) {
  if (!raw) return "—";
  let date;
  if (raw?.toDate) date = raw.toDate();
  else if (raw?.seconds) date = new Date(raw.seconds * 1000);
  else if (typeof raw === "string") date = new Date(raw);
  else return "—";
  return date.toLocaleString("es-CO", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit"
  });
}

// Cálculo de edad en base a la fecha de nacimiento
function calculateAge(birthDateStr) {
  if (!birthDateStr) return "—";
  try {
    const today = new Date();
    const birthDate = new Date(birthDateStr);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return isNaN(age) ? "—" : `${age} años`;
  } catch (_) {
    return "—";
  }
}

// Medidores de Biomarcadores Tipo PageSpeed (Horizontal HSL)
function BiomarkerGauge({ label, value, unit, percentage, description, rangeText }) {
  const [showTooltip, setShowTooltip] = useState(false);

  // Determinar color de aguja/indicador basado en el porcentaje
  const getProgressColor = (pct) => {
    if (pct < 33) return "#10B981"; // Verde óptimo
    if (pct < 66) return "#F59E0B"; // Amarillo precaución
    return "#EF4444"; // Rojo atención
  };

  return (
    <div 
      className="p-4 rounded-2xl relative transition-all duration-300 shadow-sm"
      style={{
        background: "rgba(255, 255, 255, 0.65)",
        border: "1px solid rgba(113, 100, 76, 0.1)",
      }}
    >
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-bold text-[#554C38] flex items-center gap-1 select-none">
          {label}
          {description && (
            <span 
              className="relative cursor-pointer flex items-center"
              onMouseEnter={() => setShowTooltip(true)}
              onMouseLeave={() => setShowTooltip(false)}
            >
              <Info size={12} className="text-[#A49982] hover:text-[#00628F]" />
              {showTooltip && (
                <div 
                  className="absolute z-30 bottom-full left-1/2 -translate-x-1/2 mb-2 p-3 text-xs rounded-xl shadow-xl w-60 text-left transition-all duration-300 backdrop-blur-md"
                  style={{
                    background: "rgba(24, 29, 26, 0.95)",
                    color: "#FCFAEE",
                    border: "1px solid rgba(255, 255, 255, 0.1)"
                  }}
                >
                  <p className="font-semibold mb-1 text-[#00E5FF]">{label}</p>
                  <p className="text-gray-300 text-[11px] leading-relaxed">{description}</p>
                </div>
              )}
            </span>
          )}
        </span>
        <span className="text-[10px] font-bold text-[#8B7E66]">{rangeText}</span>
      </div>

      <div className="flex items-baseline gap-1 mb-3">
        <span className="text-2xl font-black text-[#1F1914]">{value !== undefined && value !== null ? value : "—"}</span>
        {unit && <span className="text-xs font-bold text-[#8B7E66]">{unit}</span>}
      </div>

      {/* Track de medidor tricolor */}
      <div className="relative h-2 rounded-full overflow-hidden flex mb-1">
        <div className="h-full bg-emerald-500" style={{ width: "33%" }} />
        <div className="h-full bg-amber-400" style={{ width: "33%" }} />
        <div className="h-full bg-rose-500" style={{ width: "34%" }} />
      </div>

      {/* Indicador/Aguja deslizante */}
      {percentage !== undefined && percentage !== null && (
        <div className="relative w-full h-4">
          <div 
            className="absolute -top-1.5 -translate-x-1/2 flex flex-col items-center transition-all duration-500 ease-out"
            style={{ left: `${Math.min(Math.max(percentage, 2), 98)}%` }}
          >
            <div className="w-2.5 h-2.5 rotate-45" style={{ background: getProgressColor(percentage) }} />
            <div className="w-1 h-2 rounded-full" style={{ background: getProgressColor(percentage) }} />
          </div>
        </div>
      )}
    </div>
  );
}

export default function DoctorDashboard() {
  const { isMedico, isAdmin } = useUserProfile();
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState("all");
  const [users, setUsers] = useState([]);
  const [alertsByUid, setAlertsByUid] = useState({});
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [search, setSearch] = useState("");
  const [filterSeverity, setFilterSeverity] = useState("all"); // all | critical | danger | unseen

  // Modo de visualización: "table" (Tabla Dinámica de Vigilancia) | "individual" (Expediente Individual)
  const [viewMode, setViewMode] = useState("table");
  const [ihoModalUser, setIhoModalUser] = useState(null);

  // Estado del Colaborador Seleccionado (Detalle)
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedUserHistory, setSelectedUserHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [detailTab, setDetailTab] = useState("biomarkers"); // biomarkers | alerts | history

  // Carga inicial de organizaciones/clientes y colaboradores
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      // 1. Obtener todas las organizaciones (clientes)
      const clientSnap = await getDocs(collection(db, "clients"));
      let clientList = clientSnap.docs.map((d) => ({ id: d.id, ...d.data() }));

      // Registrar opción de Demostración con cohorte representativo (35 Colaboradores)
      const DEMO_ORG = {
        id: "demo-enterprise",
        name: "TechCorp Global B2B (Demostración - 35 Colaboradores)",
        isDemo: true,
      };
      if (!clientList.some((c) => c.id === DEMO_ORG.id)) {
        clientList = [DEMO_ORG, ...clientList];
      }
      setClients(clientList);

      // 2. Obtener colaboradores en base al filtro de organización
      let userList = [];
      if (selectedClient === "demo-enterprise") {
        userList = generateSimulatedCohort(35);
      } else {
        let userQuery = collection(db, "users");
        if (selectedClient !== "all") {
          userQuery = query(collection(db, "users"), where("clientId", "==", selectedClient));
        }
        const userSnap = await getDocs(userQuery);
        userList = userSnap.docs.map((d) => ({ uid: d.id, ...d.data() }));

        // Si no existen usuarios reales y se consulta "all", cargamos la cohorte demo
        if (userList.length === 0 && selectedClient === "all") {
          userList = generateSimulatedCohort(35);
        }
      }
      setUsers(userList);

      // 3. Obtener alertas de cada colaborador de forma concurrente (últimos 30 días)
      const alertMap = {};
      const now = Date.now();
      await Promise.all(userList.map(async (u) => {
        if (u.isDemo || selectedClient === "demo-enterprise" || u.uid?.startsWith("sim-emp-")) {
          alertMap[u.uid] = [];
          return;
        }
        try {
          const aSnap = await getDocs(collection(db, "users", u.uid, "alerts"));
          const uAlerts = aSnap.docs
            .map((d) => ({ id: d.id, ...d.data() }))
            .filter((a) => {
              if (a.resolved) return false;
              const expiresMs = a.expiresAt?.toMillis?.() || (a.expiresAt?.seconds ? a.expiresAt.seconds * 1000 : now + 1);
              if (expiresMs < now) return false;
              return true;
            })
            .sort((a, b) => {
              const msA = a.triggeredAt?.toMillis?.() || (a.triggeredAt?.seconds ? a.triggeredAt.seconds * 1000 : 0);
              const msB = b.triggeredAt?.toMillis?.() || (b.triggeredAt?.seconds ? b.triggeredAt.seconds * 1000 : 0);
              return msB - msA;
            });
          alertMap[u.uid] = uAlerts;
        } catch (_) {
          alertMap[u.uid] = [];
        }
      }));
      setAlertsByUid(alertMap);
      setLastRefresh(new Date());
    } catch (err) {
      console.error("Error cargando el dashboard del médico:", err);
    }
    setLoading(false);
  }, [selectedClient]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Cargar historial de mediciones del colaborador seleccionado
  useEffect(() => {
    if (!selectedUser) {
      setSelectedUserHistory([]);
      return;
    }
    setHistoryLoading(true);
    listMeasurements(selectedUser.uid, 20)
      .then((history) => setSelectedUserHistory(history || []))
      .catch((e) => console.error("Error cargando historial clínico:", e))
      .finally(() => setHistoryLoading(false));
  }, [selectedUser]);

  // Marcar una alerta como vista/revisada
  const handleMarkSeen = useCallback((userUid, alertId) => {
    // Actualización optimista síncrona
    setAlertsByUid((prev) => ({
      ...prev,
      [userUid]: (prev[userUid] || []).map((a) =>
        a.id === alertId ? { ...a, seen: true } : a
      ),
    }));
    updateDoc(doc(db, "users", userUid, "alerts", alertId), { seen: true })
      .catch((err) => console.error("Error al actualizar alerta en Firestore:", err));
  }, []);

  // Mapear el ID del cliente al nombre de la organización
  const getClientName = useCallback((id) => {
    const c = clients.find((item) => item.id === id);
    return c?.name || "Sin organización";
  }, [clients]);

  // Estadísticas del Dashboard
  const stats = useMemo(() => {
    const total = users.length;
    const activeAlerts = Object.values(alertsByUid).flat();
    const criticalCount = activeAlerts.filter((a) => a.severity === "critical" && !a.seen).length;
    const dangerCount = activeAlerts.filter((a) => a.severity === "danger" && !a.seen).length;
    const unseenCount = activeAlerts.filter((a) => !a.seen).length;
    return { total, criticalCount, dangerCount, unseenCount };
  }, [users, alertsByUid]);

  // Filtrado y búsqueda de colaboradores
  const filteredUsers = useMemo(() => {
    return users.filter((u) => {
      const uAlerts = alertsByUid[u.uid] || [];
      const hasCritical = uAlerts.some((a) => a.severity === "critical" && !a.seen);
      const hasDanger = uAlerts.some((a) => a.severity === "danger" && !a.seen);
      const hasUnseen = uAlerts.some((a) => !a.seen);

      if (filterSeverity === "critical" && !hasCritical) return false;
      if (filterSeverity === "danger" && !hasDanger) return false;
      if (filterSeverity === "unseen" && !hasUnseen) return false;

      if (!search) return true;
      const s = search.toLowerCase();
      const fullName = `${u.profile?.nombres || u.profile?.name || ""} ${u.profile?.apellidos || ""}`.toLowerCase();
      return (
        fullName.includes(s) ||
        (u.email || "").toLowerCase().includes(s)
      );
    });
  }, [users, alertsByUid, search, filterSeverity]);

  // Ordenar colaboradores: alertas críticas pendientes primero, luego peligro, luego sin alertas
  const sortedUsers = useMemo(() => {
    return [...filteredUsers].sort((a, b) => {
      const aAlerts = alertsByUid[a.uid] || [];
      const bAlerts = alertsByUid[b.uid] || [];

      const aCrit = aAlerts.filter((al) => al.severity === "critical" && !al.seen).length;
      const bCrit = bAlerts.filter((al) => al.severity === "critical" && !al.seen).length;
      if (bCrit !== aCrit) return bCrit - aCrit;

      const aDanger = aAlerts.filter((al) => al.severity === "danger" && !al.seen).length;
      const bDanger = bAlerts.filter((al) => al.severity === "danger" && !al.seen).length;
      if (bDanger !== aDanger) return bDanger - aDanger;

      const aUnseen = aAlerts.filter((al) => !al.seen).length;
      const bUnseen = bAlerts.filter((al) => !al.seen).length;
      return bUnseen - aUnseen;
    });
  }, [filteredUsers, alertsByUid]);

  // Obtener la última medición del colaborador para poblar la vista rápida de biomarcadores
  const latestMeasurementOfSelected = useMemo(() => {
    if (selectedUserHistory.length > 0) {
      return selectedUserHistory[0];
    }
    // Fallback al perfil plano si no hay histórico cargado aún
    if (selectedUser?.profile) {
      return { fields: selectedUser.profile };
    }
    return null;
  }, [selectedUser, selectedUserHistory]);

  if (!isMedico && !isAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "#FCFAEE" }}>
        <div className="text-center p-8 bg-white/80 backdrop-blur-md rounded-3xl border border-rose-100 shadow-xl max-w-sm">
          <AlertCircle size={48} className="mx-auto mb-4 text-rose-500 animate-pulse" />
          <h2 className="text-xl font-bold mb-2 text-[#1F1914]">Acceso Restringido</h2>
          <p className="text-sm text-[#71644C] leading-relaxed">
            Esta sección es exclusiva para médicos de familia y profesionales clínicos autorizados.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen relative overflow-hidden" style={{ background: "#FCFAEE" }}>
      {/* Capas decorativas de profundidad (Glows) */}
      <div 
        className="absolute top-[-20%] left-[-10%] w-[600px] h-[600px] rounded-full filter blur-[120px] pointer-events-none opacity-30"
        style={{ background: "radial-gradient(circle, rgba(42,157,143,0.15) 0%, rgba(255,255,255,0) 70%)" }}
      />
      <div 
        className="absolute bottom-[-10%] right-[-10%] w-[500px] h-[500px] rounded-full filter blur-[100px] pointer-events-none opacity-30"
        style={{ background: "radial-gradient(circle, rgba(0,98,143,0.15) 0%, rgba(255,255,255,0) 70%)" }}
      />

      {/* Header Premium del Dashboard */}
      <header className="px-6 py-5 border-b sticky top-0 z-10 backdrop-blur-md" style={{ background: "rgba(252, 250, 238, 0.8)", borderColor: "rgba(113, 100, 76, 0.08)" }}>
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex items-center gap-3.5">
            <div className="w-11 h-11 rounded-2xl flex items-center justify-center shadow-md shadow-cyan-900/5" style={{ background: "#EBF5F7", border: "1px solid rgba(0, 98, 143, 0.15)" }}>
              <ShieldCheck size={22} style={{ color: "#00628F" }} />
            </div>
            <div>
              <h1 className="text-lg font-bold text-[#1F1914] tracking-tight">Portal Clínico Familiar</h1>
              <p className="text-xs font-semibold text-[#8B7E66] flex items-center gap-1 mt-0.5">
                <Building2 size={12} />
                Monitoreo de Salud Preventivo de Colaboradores
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3 w-full sm:w-auto">
            {/* Selector de organización premium */}
            <div className="relative flex-1 sm:flex-initial">
              <select
                value={selectedClient}
                onChange={(e) => setSelectedClient(e.target.value)}
                className="w-full sm:w-64 pl-9 pr-8 py-2 rounded-xl text-xs font-bold text-[#554C38] outline-none transition-all cursor-pointer shadow-sm appearance-none border"
                style={{
                  background: "rgba(255, 255, 255, 0.7)",
                  borderColor: "rgba(113, 100, 76, 0.12)",
                }}
              >
                <option value="all">Todas las organizaciones</option>
                {clients.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
              <Building2 size={13} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#8B7E66]" />
              <ChevronDown size={13} className="absolute right-3.5 top-1/2 -translate-y-1/2 pointer-events-none text-[#8B7E66]" />
            </div>

            <button
              onClick={loadData}
              disabled={loading}
              className="p-2.5 rounded-xl border transition-all duration-300 shadow-sm cursor-pointer hover:bg-white shrink-0 active:scale-95"
              style={{
                borderColor: "rgba(113, 100, 76, 0.12)",
                background: "rgba(255, 255, 255, 0.5)"
              }}
            >
              <RefreshCw size={14} className={`text-[#554C38] ${loading ? "animate-spin" : ""}`} />
            </button>
          </div>
        </div>
      </header>

      {/* Contenido Principal */}
      <main className="max-w-7xl mx-auto px-6 py-6">
        
        {/* Bento Grid: Tarjetas de Telemetría Global */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          {[
            { label: "Colaboradores", value: stats.total, color: "#00628F", bg: "#EBF5F7", border: "rgba(0,98,143,0.1)", icon: Users },
            { label: "Alertas Activas", value: stats.unseenCount, color: "#D97706", bg: "#FEF3C7", border: "rgba(217,119,6,0.1)", icon: AlertTriangle },
            { label: "Casos Críticos", value: stats.criticalCount, color: "#DC2626", bg: "#FEE2E2", border: "rgba(220,38,38,0.1)", icon: AlertCircle },
            { label: "Alertas Revisadas", value: Object.values(alertsByUid).flat().filter(a => a.seen).length, color: "#16A34A", bg: "#DCFCE7", border: "rgba(22,163,74,0.1)", icon: CheckCircle2 }
          ].map((item, idx) => {
            const Icon = item.icon;
            return (
              <div 
                key={idx}
                className="p-5 rounded-2xl flex items-center justify-between transition-all duration-300 shadow-sm border hover:-translate-y-0.5"
                style={{
                  background: "rgba(255, 255, 255, 0.8)",
                  borderColor: item.border
                }}
              >
                <div>
                  <p className="text-[11px] font-bold text-[#8B7E66] uppercase tracking-wider">{item.label}</p>
                  <p className="text-2xl font-black mt-1.5 leading-none" style={{ color: item.color }}>{item.value}</p>
                </div>
                <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: item.bg }}>
                  <Icon size={18} style={{ color: item.color }} />
                </div>
              </div>
            );
          })}
        </div>

        {/* Selector de Modo de Vista (Vigilancia Dinámica vs Expediente Individual) */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-6 pb-2 border-b" style={{ borderColor: "rgba(113, 100, 76, 0.08)" }}>
          <div className="inline-flex p-1 rounded-2xl bg-white/70 border shadow-sm backdrop-blur-sm" style={{ borderColor: "rgba(113, 100, 76, 0.12)" }}>
            <button
              onClick={() => setViewMode("table")}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold transition-all cursor-pointer ${
                viewMode === "table"
                  ? "bg-[#00628F] text-[#FCFAEE] shadow-sm"
                  : "text-[#554C38] hover:text-[#1F1914] hover:bg-stone-100/50"
              }`}
            >
              <Users size={15} />
              Tabla Dinámica de Vigilancia
              <span className="ml-1 text-[10px] px-1.5 py-0.2 rounded-full font-black" style={{ background: viewMode === "table" ? "rgba(255,255,255,0.2)" : "rgba(0,98,143,0.1)", color: viewMode === "table" ? "#FFFFFF" : "#00628F" }}>
                7 Indicadores
              </span>
            </button>
            <button
              onClick={() => setViewMode("individual")}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold transition-all cursor-pointer ${
                viewMode === "individual"
                  ? "bg-[#00628F] text-[#FCFAEE] shadow-sm"
                  : "text-[#554C38] hover:text-[#1F1914] hover:bg-stone-100/50"
              }`}
            >
              <Stethoscope size={15} />
              Expediente Individual
              {selectedUser && (
                <span className="ml-1 text-[10px] px-1.5 py-0.2 rounded-full font-black truncate max-w-[120px]" style={{ background: viewMode === "individual" ? "rgba(255,255,255,0.2)" : "rgba(0,98,143,0.1)", color: viewMode === "individual" ? "#FFFFFF" : "#00628F" }}>
                  {selectedUser.profile?.nombres || selectedUser.profile?.name || "Seleccionado"}
                </span>
              )}
            </button>
          </div>

          {viewMode === "individual" && (
            <div className="flex items-center gap-2">
              <button
                onClick={() => setViewMode("table")}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-bold text-[#554C38] bg-white/80 border hover:bg-white transition-all shadow-sm cursor-pointer"
                style={{ borderColor: "rgba(113, 100, 76, 0.15)" }}
              >
                ← Volver a la Tabla Dinámica
              </button>
            </div>
          )}
        </div>

        {viewMode === "table" ? (
          <DoctorPopulationTable
            users={users}
            clients={clients}
            selectedClient={selectedClient}
            onSelectUserForRecord={(u) => {
              setSelectedUser(u);
              setViewMode("individual");
            }}
            onOpenIhoModal={(u) => {
              setIhoModalUser(u);
            }}
          />
        ) : (
          /* Layout Principal: Dos Columnas (Lista de Colaboradores e Historial/Métricas en Bento Grid) */
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
          
          {/* Columna Izquierda: Panel de Colaboradores */}
          <section className="lg:col-span-5 space-y-4">
            
            {/* Buscador e Interruptor de Filtros Clínicos */}
            <div 
              className="p-4 rounded-2xl border flex flex-col gap-3 shadow-sm"
              style={{
                background: "rgba(255, 255, 255, 0.7)",
                borderColor: "rgba(113, 100, 76, 0.1)",
              }}
            >
              {/* Caja de búsqueda */}
              <div className="relative">
                <input
                  type="text"
                  placeholder="Buscar colaborador por nombre o correo..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full pl-9 pr-4 py-2 rounded-xl text-xs font-semibold outline-none border transition-all"
                  style={{
                    background: "rgba(255, 255, 255, 0.8)",
                    borderColor: "rgba(113, 100, 76, 0.12)",
                    color: "#1F1914"
                  }}
                />
                <Search size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#8B7E66]" />
              </div>

              {/* Botonera de Filtro de Severidad */}
              <div className="flex flex-wrap gap-1.5">
                {[
                  { id: "all", label: "Todos", icon: Users },
                  { id: "critical", label: "Críticos", icon: AlertCircle },
                  { id: "danger", label: "Peligros", icon: AlertTriangle },
                  { id: "unseen", label: "Pendientes", icon: Clock }
                ].map((f) => {
                  const Icon = f.icon;
                  const active = filterSeverity === f.id;
                  return (
                    <button
                      key={f.id}
                      onClick={() => setFilterSeverity(f.id)}
                      className="px-3 py-1.5 rounded-lg text-[10px] font-bold flex items-center gap-1 transition-all cursor-pointer hover:bg-white border"
                      style={{
                        background: active ? "#00628F" : "rgba(255,255,255,0.4)",
                        color: active ? "#FCFAEE" : "#554C38",
                        borderColor: active ? "#00628F" : "rgba(113, 100, 76, 0.12)"
                      }}
                    >
                      <Icon size={11} />
                      {f.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Listado de Colaboradores (Cards en Bento) */}
            <div className="space-y-3 max-h-[600px] overflow-y-auto pr-1">
              {loading ? (
                <div className="py-12 text-center">
                  <Loader2 size={24} className="animate-spin mx-auto text-[#00628F] mb-2" />
                  <p className="text-xs font-medium text-[#71644C]">Cargando colaboradores...</p>
                </div>
              ) : sortedUsers.length === 0 ? (
                <div className="py-12 text-center rounded-2xl border" style={{ background: "rgba(255,255,255,0.5)", borderColor: "rgba(113, 100, 76, 0.08)" }}>
                  <Users size={28} className="mx-auto text-[#A49982] mb-2" />
                  <p className="text-xs font-bold text-[#554C38]">No se encontraron colaboradores</p>
                  <p className="text-[10px] text-[#8B7E66] mt-0.5">Pruebe ajustando los términos de búsqueda o filtros.</p>
                </div>
              ) : (
                sortedUsers.map((u) => {
                  const uAlerts = alertsByUid[u.uid] || [];
                  const criticalAlerts = uAlerts.filter((a) => a.severity === "critical" && !a.seen);
                  const dangerAlerts = uAlerts.filter((a) => a.severity === "danger" && !a.seen);
                  const unseenAlerts = uAlerts.filter((a) => !a.seen);
                  const isSelected = selectedUser?.uid === u.uid;

                  // Borde dinámico según estado clínico
                  let cardBorder = "rgba(113, 100, 76, 0.1)";
                  if (isSelected) cardBorder = "#00628F";
                  else if (criticalAlerts.length > 0) cardBorder = "#FCA5A5";
                  else if (dangerAlerts.length > 0) cardBorder = "#FED7AA";

                  const name = `${u.profile?.nombres || u.profile?.name || ""} ${u.profile?.apellidos || ""}`.trim() || u.email || "—";

                  return (
                    <div
                      key={u.uid}
                      onClick={() => setSelectedUser(u)}
                      className={`p-4 rounded-2xl cursor-pointer transition-all duration-300 border hover:-translate-y-0.5 ${isSelected ? "shadow-md bg-white" : "shadow-sm bg-white/70 hover:bg-white"}`}
                      style={{ borderColor: cardBorder }}
                    >
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-3">
                          <div 
                            className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0 shadow-sm"
                            style={{ 
                              background: criticalAlerts.length > 0 ? "#FEE2E2" : (dangerAlerts.length > 0 ? "#FFF7ED" : "#EBF5F7")
                            }}
                          >
                            <User size={16} style={{ color: criticalAlerts.length > 0 ? "#DC2626" : (dangerAlerts.length > 0 ? "#F97316" : "#00628F") }} />
                          </div>
                          <div className="min-w-0">
                            <p className="text-xs font-bold text-[#1F1914] truncate">{name}</p>
                            <p className="text-[10px] text-[#8B7E66] truncate mt-0.5">{u.email || "—"}</p>
                            <p className="text-[9px] text-[#A49982] font-semibold mt-1 flex items-center gap-1">
                              <Building2 size={9} />
                              {getClientName(u.clientId)}
                            </p>
                          </div>
                        </div>

                        {/* Badges de Alertas */}
                        <div className="flex flex-col items-end gap-1.5 shrink-0">
                          {criticalAlerts.length > 0 && (
                            <span className="text-[9px] font-black px-2 py-0.5 rounded-full" style={{ background: "#FEE2E2", color: "#DC2626" }}>
                              {criticalAlerts.length} Crítico
                            </span>
                          )}
                          {dangerAlerts.length > 0 && (
                            <span className="text-[9px] font-black px-2 py-0.5 rounded-full" style={{ background: "#FFF7ED", color: "#F97316" }}>
                              {dangerAlerts.length} Peligro
                            </span>
                          )}
                          {unseenAlerts.length > 0 && criticalAlerts.length === 0 && dangerAlerts.length === 0 && (
                            <span className="text-[9px] font-black px-2 py-0.5 rounded-full" style={{ background: "#FEFCE8", color: "#EAB308" }}>
                              {unseenAlerts.length} Pendiente
                            </span>
                          )}
                          {uAlerts.length === 0 && (
                            <span className="text-[9px] font-semibold text-emerald-600 flex items-center gap-0.5 bg-emerald-50 px-1.5 py-0.5 rounded-md">
                              <CheckCircle2 size={8} />
                              Óptimo
                            </span>
                          )}
                          <ChevronRight size={12} className="text-[#8B7E66] mt-0.5" />
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </section>

          {/* Columna Derecha: Detalle Clínico en Bento Grid */}
          <section className="lg:col-span-7">
            {selectedUser ? (
              <div 
                className="p-6 rounded-3xl border shadow-md space-y-6"
                style={{
                  background: "rgba(255, 255, 255, 0.8)",
                  borderColor: "rgba(113, 100, 76, 0.1)",
                }}
              >
                {/* Cabecera del Expediente */}
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 pb-5 border-b" style={{ borderColor: "rgba(113, 100, 76, 0.08)" }}>
                  <div className="flex items-center gap-3.5">
                    <div className="w-12 h-12 rounded-2xl flex items-center justify-center bg-cyan-50 shadow-inner">
                      <User size={20} className="text-[#00628F]" />
                    </div>
                    <div>
                      <h2 className="text-base font-black text-[#1F1914]">
                        {`${selectedUser.profile?.nombres || selectedUser.profile?.name || ""} ${selectedUser.profile?.apellidos || ""}`.trim() || selectedUser.email}
                      </h2>
                      <div className="flex flex-wrap items-center gap-2 mt-1">
                        <span className="text-[10px] font-semibold text-[#8B7E66] px-2 py-0.5 rounded-md bg-[#FAF8F0] border border-stone-200/50">
                          {selectedUser.email}
                        </span>
                        <span className="text-[10px] font-bold text-[#00628F] px-2 py-0.5 rounded-md bg-[#EBF5F7] border border-cyan-100/50">
                          {getClientName(selectedUser.clientId)}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setIhoModalUser(selectedUser)}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-bold text-amber-900 bg-amber-100 hover:bg-amber-200 border border-amber-300 transition-all shadow-sm cursor-pointer"
                      title="Emitir directiva clínica al Oficial de Salud (IHO)"
                    >
                      <ShieldAlert size={14} className="text-amber-700" />
                      <span>Directiva IHO</span>
                    </button>
                    <button 
                      onClick={() => setSelectedUser(null)}
                      className="p-1.5 rounded-lg text-[#8B7E66] hover:bg-gray-100 cursor-pointer"
                    >
                      <X size={16} />
                    </button>
                  </div>
                </div>

                {/* Sub-Bento: Datos de Perfil Planos del Colaborador */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {[
                    { label: "Edad", value: calculateAge(selectedUser.profile?.fechaNacimiento), icon: Calendar },
                    { label: "Peso", value: selectedUser.profile?.peso ? `${selectedUser.profile.peso} kg` : "—", icon: Weight },
                    { label: "Estatura", value: selectedUser.profile?.estatura || selectedUser.profile?.altura ? `${selectedUser.profile.estatura || selectedUser.profile.altura} cm` : "—", icon: Ruler },
                    { label: "Tipo de Sangre", value: selectedUser.profile?.tipoSangre || "—", icon: Activity }
                  ].map((item, idx) => {
                    const Icon = item.icon;
                    return (
                      <div key={idx} className="p-3.5 rounded-2xl bg-[#FCFAEE]/60 border border-[#71644C]/10 shadow-sm flex flex-col justify-between">
                        <div className="flex items-center justify-between">
                          <span className="text-[10px] font-bold text-[#8B7E66] uppercase">{item.label}</span>
                          <Icon size={12} className="text-[#8B7E66]" />
                        </div>
                        <span className="text-sm font-black text-[#1F1914] mt-2 block">{item.value}</span>
                      </div>
                    );
                  })}
                </div>

                {/* Navegación de Pestañas del Detalle */}
                <div className="flex border-b" style={{ borderColor: "rgba(113, 100, 76, 0.08)" }}>
                  {[
                    { id: "biomarkers", label: "Biomarcadores (13)", icon: Activity },
                    { id: "alerts", label: `Alertas (${(alertsByUid[selectedUser.uid] || []).length})`, icon: AlertTriangle },
                    { id: "history", label: "Historial de Mediciones", icon: FileText }
                  ].map((tab) => {
                    const Icon = tab.icon;
                    const active = detailTab === tab.id;
                    return (
                      <button
                        key={tab.id}
                        onClick={() => setDetailTab(tab.id)}
                        className="py-2.5 px-4 font-bold text-xs flex items-center gap-1.5 border-b-2 transition-all cursor-pointer hover:text-[#00628F]"
                        style={{
                          borderColor: active ? "#00628F" : "transparent",
                          color: active ? "#00628F" : "#8B7E66"
                        }}
                      >
                        <Icon size={12} />
                        {tab.label}
                      </button>
                    );
                  })}
                </div>

                {/* Detalle Tab 1: 13 Biomarcadores */}
                {detailTab === "biomarkers" && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {(() => {
                      const fields = latestMeasurementOfSelected?.fields || {};
                      
                      const biomarkersConfig = [
                        {
                          label: "Frecuencia Cardíaca",
                          value: fields.heartRate,
                          unit: "lpm",
                          percentage: fields.heartRate ? Math.round(((fields.heartRate - 40) / 120) * 100) : null,
                          description: "Cantidad de pulsaciones del corazón por minuto.",
                          rangeText: "60 - 100 óptimo"
                        },
                        {
                          label: "Saturación de Oxígeno (SpO2)",
                          value: fields.oximetria,
                          unit: "%",
                          percentage: fields.oximetria ? Math.round(((fields.oximetria - 90) / 10) * 100) : null,
                          description: "Nivel de oxígeno transportado en la sangre.",
                          rangeText: "95% - 100% óptimo"
                        },
                        {
                          label: "Frecuencia Respiratoria",
                          value: fields.respirationRate,
                          unit: "rpm",
                          percentage: fields.respirationRate ? Math.round(((fields.respirationRate - 10) / 20) * 100) : null,
                          description: "Número de respiraciones completadas en un minuto.",
                          rangeText: "12 - 20 óptimo"
                        },
                        {
                          label: "Variabilidad Cardíaca (HRV)",
                          value: fields.heartRateVariability,
                          unit: "ms",
                          percentage: fields.heartRateVariability ? Math.round((fields.heartRateVariability / 100) * 100) : null,
                          description: "Variación en los intervalos de tiempo entre latidos consecutivos.",
                          rangeText: ">40ms óptimo"
                        },
                        {
                          label: "Índice de Estrés de Baevsky",
                          value: fields.stressIndex,
                          unit: "",
                          percentage: fields.stressIndex ? Math.round((fields.stressIndex / 1000) * 100) : null,
                          description: "Carga psicofisiológica calculada en base a la escala clínica oficial de Baevsky (0-1000).",
                          rangeText: "0 - 150 normal"
                        },
                        {
                          label: "Presión Sistólica",
                          value: fields.paSistolica,
                          unit: "mmHg",
                          percentage: fields.paSistolica ? Math.round(((fields.paSistolica - 90) / 70) * 100) : null,
                          description: "Presión arterial durante la contracción del corazón.",
                          rangeText: "90 - 120 normal"
                        },
                        {
                          label: "Presión Diastólica",
                          value: fields.paDiastolica,
                          unit: "mmHg",
                          percentage: fields.paDiastolica ? Math.round(((fields.paDiastolica - 60) / 40) * 100) : null,
                          description: "Presión arterial en las arterias entre latidos.",
                          rangeText: "60 - 80 normal"
                        },
                        {
                          label: "Glucemia en Ayunas",
                          value: fields.glucemia,
                          unit: "mg/dL",
                          percentage: fields.glucemia ? Math.round(((fields.glucemia - 60) / 80) * 100) : null,
                          description: "Concentración de glucosa en el torrente sanguíneo.",
                          rangeText: "70 - 100 normal"
                        },
                        {
                          label: "Edad Cardiovascular",
                          value: fields.cardiovascularAge,
                          unit: "años",
                          percentage: fields.cardiovascularAge ? Math.round((fields.cardiovascularAge / 100) * 100) : null,
                          description: "Estimación biológica de la edad del sistema vascular.",
                          rangeText: "Alrededor de edad real"
                        },
                        {
                          label: "IMC Cardiovascular",
                          value: fields.cardiovascularBMI,
                          unit: "",
                          percentage: fields.cardiovascularBMI ? Math.round(((fields.cardiovascularBMI - 15) / 25) * 100) : null,
                          description: "Cálculo biométrico de la relación del peso corporal para la salud vascular.",
                          rangeText: "18.5 - 24.9 normal"
                        },
                        {
                          label: "Sistema Parasimpático (PNS)",
                          value: fields.ansIndexPNS,
                          unit: "",
                          percentage: fields.ansIndexPNS ? Math.round(((fields.ansIndexPNS + 4) / 8) * 100) : null,
                          description: "Indicador del estado de relajación y regeneración autónoma.",
                          rangeText: "Valores positivos óptimo"
                        },
                        {
                          label: "Sistema Simpático (SNS)",
                          value: fields.ansIndexSNS,
                          unit: "",
                          percentage: fields.ansIndexSNS ? Math.round(((fields.ansIndexSNS + 4) / 8) * 100) : null,
                          description: "Indicador clínico del estado de alerta y lucha del organismo.",
                          rangeText: "Valores neutros óptimo"
                        },
                        {
                          label: "Puntaje de Bienestar",
                          value: fields.wellnessScore,
                          unit: "/10",
                          percentage: fields.wellnessScore ? Math.round((fields.wellnessScore / 10) * 100) : null,
                          description: "Calificación consolidada del estado general de salud y homeostasis.",
                          rangeText: "7 - 10 óptimo"
                        }
                      ];

                      // Sinceridad de Datos: si el usuario no tiene registros históricos y no es demo, desenfocar métricas
                      const hasMetrics = selectedUserHistory.length > 0 || selectedUser.profile?.peso;
                      return (
                        <div className="col-span-full relative">
                          <div className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 ${hasMetrics ? "" : "blur-sm select-none pointer-events-none"}`}>
                            {biomarkersConfig.map((bio, index) => (
                              <BiomarkerGauge
                                key={index}
                                label={bio.label}
                                value={bio.value}
                                unit={bio.unit}
                                percentage={bio.percentage}
                                description={bio.description}
                                rangeText={bio.rangeText}
                              />
                            ))}
                          </div>
                          {!hasMetrics && (
                            <div className="absolute inset-0 flex items-center justify-center bg-transparent z-10">
                              <div className="text-center p-6 bg-white/95 backdrop-blur-md rounded-2xl border max-w-sm shadow-xl" style={{ borderColor: "rgba(113, 100, 76, 0.12)" }}>
                                <Activity size={32} className="mx-auto text-[#00628F] mb-2 animate-bounce" />
                                <p className="text-xs font-bold text-[#1F1914]">Sin Mediciones Registradas</p>
                                <p className="text-[10px] text-[#71644C] leading-relaxed mt-1">
                                  El colaborador no ha registrado mediciones biométricas en la plataforma. Invite al colaborador a realizar su primer escaneo clínico.
                                </p>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                )}

                {/* Detalle Tab 2: Gestión de Alertas */}
                {detailTab === "alerts" && (
                  <div className="space-y-3.5">
                    {(() => {
                      const uAlerts = alertsByUid[selectedUser.uid] || [];
                      if (uAlerts.length === 0) {
                        return (
                          <div className="py-12 text-center rounded-2xl bg-[#FCFAEE]/50 border" style={{ borderColor: "rgba(113, 100, 76, 0.08)" }}>
                            <CheckCircle2 size={32} className="mx-auto text-emerald-500 mb-2" />
                            <p className="text-xs font-bold text-[#1F1914]">¡Todo en Orden!</p>
                            <p className="text-[10px] text-[#8B7E66] mt-0.5">El colaborador no presenta alertas clínicas activas en los últimos 30 días.</p>
                          </div>
                        );
                      }
                      return uAlerts.map((a) => {
                        const cfg = SEVERITY_CONFIG[a.severity] || SEVERITY_CONFIG.warning;
                        const Icon = cfg.icon;
                        return (
                          <div
                            key={a.id}
                            className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 p-4 rounded-xl border transition-all"
                            style={{
                              borderColor: a.seen ? "rgba(113,100,76,0.1)" : cfg.border,
                              background: a.seen ? "rgba(255,255,255,0.4)" : "white"
                            }}
                          >
                            <div className="flex items-start gap-3">
                              <div className="p-1.5 rounded-lg mt-0.5 shrink-0" style={{ background: cfg.bg }}>
                                <Icon size={14} style={{ color: cfg.color }} />
                              </div>
                              <div>
                                <p className="text-xs font-bold text-[#1F1914]">{a.doctorText || a.text}</p>
                                <div className="flex items-center gap-2 mt-1.5">
                                  <SeverityBadge severity={a.severity} />
                                  <span className="text-[9px] text-[#8B7E66] font-semibold flex items-center gap-0.5">
                                    <Clock size={10} />
                                    {fmtDateTime(a.triggeredAt)}
                                  </span>
                                </div>
                              </div>
                            </div>

                            {/* Botón de marcar como revisado */}
                            {!a.seen ? (
                              <button
                                onClick={() => handleMarkSeen(selectedUser.uid, a.id)}
                                className="px-3 py-1.5 rounded-lg text-[10px] font-black cursor-pointer transition-all hover:scale-95 flex items-center gap-1 shrink-0"
                                style={{
                                  background: "#E8F5E9",
                                  color: "#2E7D32",
                                  border: "1px solid #C8E6C9"
                                }}
                              >
                                <CheckCircle2 size={11} />
                                Marcar Revisado
                              </button>
                            ) : (
                              <span className="text-[10px] font-bold text-emerald-600 flex items-center gap-1 bg-emerald-50 px-2.5 py-1 rounded-lg border border-emerald-100">
                                <UserCheck size={11} />
                                Revisado
                              </span>
                            )}
                          </div>
                        );
                      });
                    })()}
                  </div>
                )}

                {/* Detalle Tab 3: Historial de Mediciones */}
                {detailTab === "history" && (
                  <div className="space-y-3">
                    {historyLoading ? (
                      <div className="py-12 text-center">
                        <Loader2 size={24} className="animate-spin mx-auto text-[#00628F] mb-2" />
                        <p className="text-xs font-medium text-[#71644C]">Cargando historial clínico...</p>
                      </div>
                    ) : selectedUserHistory.length === 0 ? (
                      <div className="py-12 text-center rounded-2xl bg-[#FCFAEE]/50 border" style={{ borderColor: "rgba(113, 100, 76, 0.08)" }}>
                        <FileText size={32} className="mx-auto text-[#A49982] mb-2" />
                        <p className="text-xs font-bold text-[#1F1914]">Sin Historial</p>
                        <p className="text-[10px] text-[#8B7E66] mt-0.5">No se encontraron mediciones clínicas registradas para este colaborador.</p>
                      </div>
                    ) : (
                      <div className="border rounded-2xl overflow-hidden" style={{ borderColor: "rgba(113, 100, 76, 0.08)" }}>
                        <table className="w-full text-left border-collapse">
                          <thead>
                            <tr className="bg-[#FAF8F0] border-b text-[10px] font-bold text-[#8B7E66] uppercase" style={{ borderColor: "rgba(113, 100, 76, 0.08)" }}>
                              <th className="p-3.5">Fecha</th>
                              <th className="p-3.5">Origen / Método</th>
                              <th className="p-3.5">Métricas Clave</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y text-xs" style={{ divideColor: "rgba(113, 100, 76, 0.05)" }}>
                            {selectedUserHistory.map((m) => {
                              const keys = Object.keys(m.fields || {}).filter(k => k !== "cvdRisk"); // Esconder riesgo CVD de forma sincera
                              return (
                                <tr key={m.id} className="hover:bg-white/40 transition-colors">
                                  <td className="p-3.5 font-bold text-[#1F1914]">
                                    {fmtDateTime(m.createdAt)}
                                  </td>
                                  <td className="p-3.5 font-semibold text-[#554C38] capitalize">
                                    <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md bg-stone-100 text-stone-700 border border-stone-200/50">
                                      {m.method === "camera" ? "Escaneo rPPG" : "Registro Manual"}
                                    </span>
                                  </td>
                                  <td className="p-3.5 text-[#554C38]">
                                    <div className="flex flex-wrap gap-1">
                                      {keys.slice(0, 4).map((k) => (
                                        <span key={k} className="inline-block text-[10px] font-bold px-1.5 py-0.5 rounded bg-[#FAF8F0] border border-stone-200 text-stone-700">
                                          {k}: {m.fields[k]}
                                        </span>
                                      ))}
                                      {keys.length > 4 && (
                                        <span className="text-[10px] text-[#8B7E66] font-bold">
                                          +{keys.length - 4} más
                                        </span>
                                      )}
                                    </div>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ) : (
              // Estado inicial: invitación a seleccionar un colaborador
              <div 
                className="py-24 text-center rounded-3xl border shadow-sm flex flex-col items-center justify-center space-y-3.5"
                style={{
                  background: "rgba(255, 255, 255, 0.5)",
                  borderColor: "rgba(113, 100, 76, 0.08)",
                }}
              >
                <div className="w-14 h-14 rounded-2xl flex items-center justify-center bg-[#EBF5F7] shadow-inner">
                  <Activity size={24} className="text-[#00628F]" />
                </div>
                <div>
                  <p className="text-sm font-black text-[#1F1914]">Seleccione un Colaborador</p>
                  <p className="text-xs text-[#71644C] max-w-xs mt-1 leading-relaxed">
                    Elija un colaborador de la lista para cargar su expediente clínico, evaluar sus biomarcadores preventivos e interactuar con sus alertas activas.
                  </p>
                </div>
              </div>
            )}
          </section>

        </div>
      )}
      </main>

      {/* Modal de Coordinación y Directivas Clínicas con el IHO */}
      {ihoModalUser && (
        <IhoCoordinationModal
          user={ihoModalUser}
          onClose={() => setIhoModalUser(null)}
          onSuccess={() => {
            // Directiva registrada con éxito
          }}
        />
      )}
    </div>
  );
}
